import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, MessageCircle, MapPin, Briefcase, Building } from 'lucide-react';
import { FounderBadge } from '../components/FounderBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberProfile() {
  const { userId } = useParams();
  const { user, isActiveMember } = useAuth();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isActiveMember) return;
    const fetchMember = async () => {
      try {
        const res = await fetch(`${API}/community/members/${userId}`, { credentials: 'include' });
        if (res.ok) setMember(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchMember();
  }, [userId, isActiveMember]);

  if (!isActiveMember) { navigate('/login'); return null; }
  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-500">Loading...</p></div>;
  if (!member) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-500">Member not found</p></div>;

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="member-profile-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl">
        <Link to="/community" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#3D4F7A] mb-6">
          <ArrowLeft className="w-4 h-4" />Back to Community
        </Link>
        <Card className="border border-slate-200 shadow-lg">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center mb-6">
              <Avatar className="w-20 h-20 mb-4">
                <AvatarImage src={member.picture} />
                <AvatarFallback className="bg-[#3D4F7A] text-white text-2xl">{member.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <h1 className="font-heading text-2xl font-bold text-slate-900 inline-flex items-center gap-2">{member.name} <FounderBadge tier={member.membership_tier} size={22} /></h1>
              {member.member_id && <p className="text-xs text-slate-400 mt-1">ID: {member.member_id}</p>}
            </div>
            <div className="space-y-3 mb-6">
              {member.user_role && (
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Briefcase className="w-4 h-4 text-slate-400" />{member.user_role}
                </div>
              )}
              {member.brokerage_name && (
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Building className="w-4 h-4 text-slate-400" />{member.brokerage_name}
                </div>
              )}
              {member.license_state && (
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />{member.license_state}
                </div>
              )}
            </div>
            {user?.user_id !== userId && (
              <Link to={`/community/messages?with=${userId}`}>
                <Button className="w-full bg-[#3D4F7A] hover:bg-[#2A3755]" data-testid="message-member-btn">
                  <MessageCircle className="w-4 h-4 mr-2" />Send Message
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
